import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Header
    tagline: "Standing Together",
    
    // Hero
    heroTitle: "The UAE is",
    heroSubtitle: "Share your message of love with the United Arab Emirates",
    
    // Form
    formTitle: "Share Your Love",
    namePlaceholder: "Your Name",
    emailPlaceholder: "Your Email",
    textPlaceholder: "Complete the sentence: The UAE is... (max 300 characters)",
    uploadLabel: "Upload Image or Video (optional)",
    uploadHint: "Max 10MB - JPG, PNG, GIF, MP4, WebM",
    dragDrop: "Drag & drop or click to upload",
    submitButton: "Share Your Message",
    submitting: "Submitting...",
    
    // Success
    successTitle: "Thank You!",
    successMessage: "Your message has been submitted and is pending approval.",
    submitAnother: "Submit Another",
    
    // Gallery
    galleryTitle: "Messages of Love",
    noMessages: "Be the first to share your love",
    loadingMessages: "Loading messages...",
    
    // Admin
    adminTitle: "Admin Panel",
    adminLogin: "Admin Login",
    passwordPlaceholder: "Enter admin password",
    loginButton: "Login",
    logout: "Logout",
    pendingPosts: "Pending Posts",
    allPosts: "All Posts",
    approve: "Approve",
    reject: "Reject",
    delete: "Delete",
    noPending: "No pending posts",
    
    // Footer
    footerText: "Standing with UAE",
    
    // Errors
    errorName: "Please enter your name",
    errorEmail: "Please enter a valid email",
    errorText: "Please complete the sentence",
    errorTextLength: "Text must be 300 characters or less",
    errorFileSize: "File size must be under 10MB",
    errorFileType: "Please upload an image or video file",
    errorSubmit: "Failed to submit. Please try again.",
    
    // Stats
    messagesShared: "Messages Shared"
  },
  ar: {
    // Header
    tagline: "نقف معاً",
    
    // Hero
    heroTitle: "الإمارات هي",
    heroSubtitle: "شارك رسالة حبك مع الإمارات العربية المتحدة",
    
    // Form
    formTitle: "شارك حبك",
    namePlaceholder: "اسمك",
    emailPlaceholder: "بريدك الإلكتروني",
    textPlaceholder: "أكمل الجملة: الإمارات هي... (300 حرف كحد أقصى)",
    uploadLabel: "ارفع صورة أو فيديو (اختياري)",
    uploadHint: "10 ميجا بايت كحد أقصى - JPG, PNG, GIF, MP4, WebM",
    dragDrop: "اسحب وأفلت أو انقر للرفع",
    submitButton: "شارك رسالتك",
    submitting: "جارٍ الإرسال...",
    
    // Success
    successTitle: "شكراً لك!",
    successMessage: "تم إرسال رسالتك وهي قيد المراجعة.",
    submitAnother: "إرسال أخرى",
    
    // Gallery
    galleryTitle: "رسائل الحب",
    noMessages: "كن أول من يشارك حبه",
    loadingMessages: "جارٍ تحميل الرسائل...",
    
    // Admin
    adminTitle: "لوحة الإدارة",
    adminLogin: "تسجيل دخول المدير",
    passwordPlaceholder: "أدخل كلمة مرور المدير",
    loginButton: "تسجيل الدخول",
    logout: "تسجيل الخروج",
    pendingPosts: "المنشورات المعلقة",
    allPosts: "جميع المنشورات",
    approve: "موافقة",
    reject: "رفض",
    delete: "حذف",
    noPending: "لا توجد منشورات معلقة",
    
    // Footer
    footerText: "نقف مع الإمارات",
    
    // Errors
    errorName: "الرجاء إدخال اسمك",
    errorEmail: "الرجاء إدخال بريد إلكتروني صحيح",
    errorText: "الرجاء إكمال الجملة",
    errorTextLength: "يجب أن يكون النص 300 حرف أو أقل",
    errorFileSize: "يجب أن يكون حجم الملف أقل من 10 ميجابايت",
    errorFileType: "الرجاء رفع صورة أو فيديو",
    errorSubmit: "فشل الإرسال. حاول مرة أخرى.",
    
    // Stats
    messagesShared: "رسائل مشاركة"
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('uae-lang');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('uae-lang', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => translations[language][key] || key;

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
